import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { AuthApiService, LoginResponse } from '../../services/auth-api.service';
import { I18nService } from '../../services/i18n.service';
import { AuthCardComponent } from '../../ui/auth-card.component';

@Component({
  selector: 'auth-login',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, AuthCardComponent],
  template: `
    <auth-card [title]="i18n.t('login.heading')" [subtitle]="i18n.t('login.subtitle')">
      @if (loggedOut()) {
        <p class="au-alert au-alert--ok">{{ i18n.t('login.logout') }}</p>
      }
      @if (errorCode()) {
        <p class="au-alert au-alert--error">{{ i18n.t('login.error.' + errorCode()) }}</p>
      }

      <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
        <div class="au-field">
          <label class="au-label" for="email">{{ i18n.t('login.identifier') }}</label>
          <input id="email" type="text" autocomplete="username" required autofocus
                 class="au-input"
                 [class.au-input--error]="invalid('email')"
                 [formControl]="form.controls.email">
          @if (invalid('email')) {
            <span class="au-error">{{ i18n.t('common.required') }}</span>
          }
        </div>

        <div class="au-field">
          <label class="au-label" for="password">{{ i18n.t('login.password') }}</label>
          <input id="password" [type]="showPassword() ? 'text' : 'password'"
                 autocomplete="current-password" required minlength="6"
                 class="au-input"
                 [class.au-input--error]="invalid('password')"
                 [formControl]="form.controls.password">
          <button type="button" class="au-toggle"
                  (click)="showPassword.set(!showPassword())"
                  [attr.aria-label]="showPassword() ? i18n.t('login.hide_password') : i18n.t('login.show_password')">
            @if (showPassword()) {
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 3l18 18"/>
                <path d="M10.58 10.58a2 2 0 002.83 2.83"/>
                <path d="M9.88 4.24A10.94 10.94 0 0112 4c7 0 10 8 10 8a13.16 13.16 0 01-1.67 2.68"/>
                <path d="M6.61 6.61A13.526 13.526 0 002 12s3 8 10 8a10.94 10.94 0 005.76-1.61"/>
              </svg>
            } @else {
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            }
          </button>
        </div>

        <button type="submit" class="au-button" [disabled]="loading()">
          {{ loading() ? i18n.t('common.loading') : i18n.t('login.submit') }}
        </button>
      </form>

      <p class="au-links">
        <a [routerLink]="['/', langPath, 'forgot-password']">{{ i18n.t('login.forgot') }}</a>
        <span class="au-links__sep"> · </span>
        <a [routerLink]="['/', langPath, 'register']">{{ i18n.t('login.register') }}</a>
      </p>
    </auth-card>
  `,
  styles: [`
    .au-field { position: relative; }
    .au-toggle {
      position: absolute;
      right: 8px;
      top: 26px;
      background: none;
      border: none;
      cursor: pointer;
      color: var(--au-text-soft);
      padding: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      transition: color .15s, background-color .15s;
    }
    .au-toggle:hover { color: var(--au-primary); background: var(--au-bg-soft, rgba(0,0,0,.04)); }
    .au-toggle svg { display: block; }
  `],
})
export class LoginComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private api = inject(AuthApiService);
  protected i18n = inject(I18nService);

  protected langPath: 'ru' | 'kz' = 'ru';

  readonly form = new FormGroup({
    // Логин принимает email ИЛИ произвольный идентификатор (не-email юзеры,
    // заведённые админом). Поэтому без Validators.email — только required.
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    password: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(6)] }),
  });

  readonly loading = signal(false);
  readonly showPassword = signal(false);
  readonly errorCode = signal<string>('');
  readonly loggedOut = signal(false);

  invalid(name: 'email' | 'password'): boolean {
    const c = this.form.controls[name];
    return c.invalid && (c.dirty || c.touched);
  }

  async ngOnInit(): Promise<void> {
    const langParam = this.route.snapshot.paramMap.get('lang');
    this.langPath = langParam === 'kz' ? 'kz' : 'ru';
    await this.i18n.use(I18nService.normaliseFromUrl(langParam));

    // Pre-fill alerts from query (?error=bad_credentials, ?logout)
    const qp = this.route.snapshot.queryParamMap;
    const err = qp.get('error');
    if (err) this.errorCode.set(err);
    if (qp.has('logout')) this.loggedOut.set(true);

    // Если юзер пришёл на /login прямо с активной SSO-сессией — не
    // показываем форму, а ведём на /account, где можно «продолжить» или
    // «выйти». Не делаем редирект если есть ?logout/?error (после
    // logout'а или ошибки честно показываем форму).
    if (!qp.has('logout') && !err) {
      this.api.getSessionInfo().subscribe({
        next: (resp) => {
          if (resp.authenticated) {
            this.router.navigate(['/', this.langPath, 'account']);
          }
        },
        error: () => { /* anonymous — остаёмся на форме */ },
      });
    }
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.errorCode.set('');

    this.api.login({
      email: this.form.value.email!,
      password: this.form.value.password!,
    }).subscribe({
      next: (resp: LoginResponse) => {
        if (resp.ok) {
          // continueUrl: либо SavedRequest (/oauth2/authorize?code=...) либо frontend.
          window.location.href = resp.continueUrl || '/';
        } else {
          this.loading.set(false);
          this.errorCode.set(resp.errorCode || 'bad_credentials');
        }
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        // Backend возвращает 401/403 с {ok:false, errorCode}, но если по какой-то
        // причине пришла другая ошибка — показываем generic.
        this.errorCode.set(err.error?.errorCode || 'bad_credentials');
      },
    });
  }
}
